import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import FilterBar, { buildQueryString } from "../components/dashboard/FilterBar";
import {
  KpiStrip,
  IssueBreakdownChart,
  ServiceLineDistributionChart,
  CaseRateChart,
  VolumeTrendChart,
  TimeToResolutionChart,
  StatusDistributionChart,
  RepeatCasesTables,
  CasesByPartnerChart,
  CasesByDepartmentChart,
  OutcomesDistributionChart,
  TerminationTrendChart,
  PipTrendChart,
  AttendanceHotspotsChart,
} from "../components/dashboard/charts";
import { AiSummaryStrip } from "../components/insights/panels";
import MyReviewQueueCard from "../components/dashboard/MyReviewQueueCard";

const DEFAULT_FILTERS = {
  service_lines: [],
  issue_types: [],
  statuses: [],
  start_date: "",
  end_date: "",
  preset: "30d",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [issueTypes, setIssueTypes] = useState([]);
  const [granularity, setGranularity] = useState("monthly");

  const [summary, setSummary] = useState(null);
  const [issueBreakdown, setIssueBreakdown] = useState(null);
  const [slDistribution, setSlDistribution] = useState(null);
  const [caseRate, setCaseRate] = useState(null);
  const [volumeTrend, setVolumeTrend] = useState(null);
  const [ttr, setTtr] = useState(null);
  const [statusDist, setStatusDist] = useState(null);
  const [repeatEmp, setRepeatEmp] = useState(null);
  const [repeatDept, setRepeatDept] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  // Phase 4c
  const [casesByPartner, setCasesByPartner] = useState(null);
  const [casesByDept, setCasesByDept] = useState(null);
  const [outcomesDist, setOutcomesDist] = useState(null);
  const [terminationTrend, setTerminationTrend] = useState(null);
  const [pipTrend, setPipTrend] = useState(null);
  const [attendanceHotspots, setAttendanceHotspots] = useState(null);
  // Phase 2 (May 2026) — admin-only "Removed Records (30d)" tile
  const [removedRecords, setRemovedRecords] = useState(null);

  // Removed-records visibility: HR admins or Compliance/legal_review.
  // Server enforces the same rule; this just hides the tile + skips the fetch.
  const showRemovedRecords = Boolean(
    (user?.role === "hr" && user?.is_admin)
      || user?.role === "compliance"
      || user?.role === "legal_review",
  );

  const [loadingKey, setLoadingKey] = useState(0); // bumps to trigger reload

  // Build query string from current filters (excluding preset — that's UI-only)
  const queryString = useMemo(() => buildQueryString(filters), [filters]);

  useEffect(() => {
    // Taxonomy for filter options
    api
      .get("/config/issue-taxonomy")
      .then((r) => setIssueTypes(Object.keys(r.data?.taxonomy || {})))
      .catch(() => setIssueTypes([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const qs = queryString ? `?${queryString}` : "";

    // Reset panels so skeletons flash
    setSummary(null);
    setIssueBreakdown(null);
    setSlDistribution(null);
    setCaseRate(null);
    setVolumeTrend(null);
    setTtr(null);
    setStatusDist(null);
    setRepeatEmp(null);
    setRepeatDept(null);
    setAiSummary(null);
    setCasesByPartner(null);
    setCasesByDept(null);
    setOutcomesDist(null);
    setTerminationTrend(null);
    setPipTrend(null);
    setAttendanceHotspots(null);
    setRemovedRecords(null);

    const filtersBody = {};
    for (const [k, v] of new URLSearchParams(queryString).entries()) {
      filtersBody[k] = v;
    }

    // Phase 4c — Outcome Analytics widgets are inherently long-window trend
    // widgets, so they ignore the FilterBar's date range and pin to a rolling
    // 365-day window. Other filters (service_lines, issue_types) still apply.
    const trendQs = (() => {
      const params = new URLSearchParams();
      // Carry over non-date filters
      if (filters.service_lines?.length) params.set("service_lines", filters.service_lines.join(","));
      if (filters.issue_types?.length) params.set("issue_types", filters.issue_types.join(","));
      const today = new Date();
      const yearAgo = new Date(today);
      yearAgo.setDate(today.getDate() - 365);
      params.set("start_date", yearAgo.toISOString().slice(0, 10));
      params.set("end_date", today.toISOString().slice(0, 10));
      return `?${params.toString()}`;
    })();

    const fetchers = [
      api.get(`/dashboard/summary${qs}`).then((r) => !cancelled && setSummary(r.data)),
      api.get(`/dashboard/issue-breakdown${qs}`).then((r) => !cancelled && setIssueBreakdown(r.data)),
      api.get(`/dashboard/service-line-distribution${qs}`).then((r) => !cancelled && setSlDistribution(r.data)),
      api.get(`/dashboard/case-rate${qs}`).then((r) => !cancelled && setCaseRate(r.data)),
      api
        .get(`/dashboard/volume-trend${qs ? qs + "&" : "?"}granularity=${granularity}`)
        .then((r) => !cancelled && setVolumeTrend(r.data)),
      api.get(`/dashboard/time-to-resolution${qs}`).then((r) => !cancelled && setTtr(r.data)),
      api.get(`/dashboard/status-distribution${qs}`).then((r) => !cancelled && setStatusDist(r.data)),
      api
        .get(`/dashboard/repeat-cases${qs ? qs + "&" : "?"}by=employee`)
        .then((r) => !cancelled && setRepeatEmp(r.data)),
      api
        .get(`/dashboard/repeat-cases${qs ? qs + "&" : "?"}by=manager_or_dept`)
        .then((r) => !cancelled && setRepeatDept(r.data)),
      api
        .post("/insights/narrative", { filters: filtersBody })
        .then((r) => !cancelled && setAiSummary(r.data))
        .catch(() => {}),
      // Phase 4c — Cases-by widgets follow the global window
      api.get(`/dashboard/cases-by-partner${qs}`).then((r) => !cancelled && setCasesByPartner(r.data)),
      api.get(`/dashboard/cases-by-department${qs}`).then((r) => !cancelled && setCasesByDept(r.data)),
      // Phase 4c — Outcome Analytics: pinned 365-day window for meaningful trends
      api.get(`/dashboard/outcomes-distribution${trendQs}`).then((r) => !cancelled && setOutcomesDist(r.data)),
      api.get(`/dashboard/termination-trend${trendQs}`).then((r) => !cancelled && setTerminationTrend(r.data)),
      api.get(`/dashboard/pip-trend${trendQs}`).then((r) => !cancelled && setPipTrend(r.data)),
      api.get(`/dashboard/attendance-hotspots${qs}`).then((r) => !cancelled && setAttendanceHotspots(r.data)),
      // Phase 2 (May 2026) — Removed Records tile. Admin-only; we skip the
      // fetch entirely for non-admin roles so we don't burn rate-limit quota
      // on a request that's guaranteed to 403. Filters don't apply — this is
      // a fixed 30-day audit-log window.
      ...(showRemovedRecords
        ? [
            api
              .get(`/dashboard/removed-records?days=30`)
              .then((r) => !cancelled && setRemovedRecords(r.data))
              .catch(() => !cancelled && setRemovedRecords({
                window_days: 30, archived: 0, deleted: 0, total: 0, error: true,
              })),
          ]
        : []),
    ];

    Promise.all(fetchers).catch((err) => {
      if (!cancelled) toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, granularity, loadingKey]);

  // Drill-down → navigate to /cases with equivalent URL params.
  // Active dashboard filters are carried along so the cases list respects them.
  const drillTo = (patch = {}) => {
    const params = new URLSearchParams();
    const merged = {
      service_lines: patch.service_lines ?? filters.service_lines,
      issue_types: patch.issue_types ?? filters.issue_types,
      statuses: patch.statuses ?? filters.statuses,
      start_date: patch.start_date ?? filters.start_date,
      end_date: patch.end_date ?? filters.end_date,
    };
    if (merged.service_lines.length) params.set("service_lines", merged.service_lines.join(","));
    if (merged.issue_types.length) params.set("issue_types", merged.issue_types.join(","));
    if (merged.statuses.length) params.set("statuses", merged.statuses.join(","));
    if (patch.high_risk) params.set("high_risk", "1");
    if (patch.sla_breach) params.set("sla_breach", "1");
    // Phase 4c drill-through filters
    if (patch.overdue) params.set("overdue", "true");
    if (patch.follow_up_due) params.set("follow_up_due", patch.follow_up_due);
    if (patch.stale) params.set("stale", "true");
    if (patch.mine) params.set("mine", "1");
    if (patch.assigned_hr_partner_id) params.set("assigned_hr_partner_id", patch.assigned_hr_partner_id);
    if (patch.department) params.set("department", patch.department);
    if (merged.start_date) params.set("start_date", merged.start_date);
    if (merged.end_date) params.set("end_date", merged.end_date);
    navigate(`/cases?${params.toString()}`);
  };

  const drillEmployee = (e) => {
    const params = new URLSearchParams();
    if (filters.start_date) params.set("start_date", filters.start_date);
    if (filters.end_date) params.set("end_date", filters.end_date);
    if (e.file_number != null) params.set("file_number", String(e.file_number));
    else params.set("q", e.name || "");
    navigate(`/cases?${params.toString()}`);
  };

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1
            className="font-serif text-3xl font-semibold tracking-tight"
            style={{ letterSpacing: "-0.015em", color: "var(--page-fg)" }}
          >
            Dashboard
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--page-fg-secondary)" }}>
            {user?.role === "manager"
              ? "Scoped to your assigned team."
              : "Real-time view of Employee Relations activity across Goodwin Living."}
          </p>
        </div>
      </div>

      <FilterBar issueTypes={issueTypes} onFiltersChange={setFilters} />

      <AiSummaryStrip
        data={aiSummary}
        loading={!aiSummary}
        filtersQuery={queryString}
      />

      <KpiStrip
        data={summary}
        loading={!summary}
        onDrill={drillTo}
        removedRecords={showRemovedRecords ? removedRecords : null}
        onRemovedClick={() => navigate(`/audit-logs?entity_type=case`)}
      />

      {/* Phase 11 — leadership / compliance / legal review review queue widget */}
      <MyReviewQueueCard user={user} />

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <IssueBreakdownChart
          data={issueBreakdown}
          loading={!issueBreakdown}
          onDrill={drillTo}
        />
        <ServiceLineDistributionChart
          data={slDistribution}
          loading={!slDistribution}
          onDrill={drillTo}
        />
        <CaseRateChart data={caseRate} loading={!caseRate} onDrill={drillTo} />
        <VolumeTrendChart
          data={volumeTrend}
          loading={!volumeTrend}
          granularity={granularity}
          onGranularityChange={setGranularity}
        />
        <TimeToResolutionChart data={ttr} loading={!ttr} />
        <StatusDistributionChart
          data={statusDist}
          loading={!statusDist}
          onDrill={drillTo}
        />
      </div>

      <div className="mt-6">
        <RepeatCasesTables
          employeeData={repeatEmp}
          deptData={repeatDept}
          loading={!repeatEmp || !repeatDept}
          onDrillEmployee={drillEmployee}
        />
      </div>

      {/* Phase 4c — Cases-by row */}
      <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="cases-by-row">
        <CasesByPartnerChart
          data={casesByPartner}
          loading={!casesByPartner}
          onDrill={drillTo}
        />
        <CasesByDepartmentChart
          data={casesByDept}
          loading={!casesByDept}
          onDrill={drillTo}
        />
      </div>

      {/* Phase 4c — Outcome Analytics */}
      <div className="mt-8" data-testid="outcome-analytics-row">
        <div className="mb-3 flex items-baseline gap-3">
          <h2
            className="font-serif text-xl font-semibold"
            style={{ letterSpacing: "-0.01em", color: "var(--page-fg)" }}
          >
            Outcome Analytics
          </h2>
          <span className="text-[11px]" style={{ color: "var(--page-fg-muted)" }}>
            Rolling 12-month window — service-line / issue-type filters still apply
          </span>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <OutcomesDistributionChart data={outcomesDist} loading={!outcomesDist} />
          <TerminationTrendChart data={terminationTrend} loading={!terminationTrend} />
          <PipTrendChart data={pipTrend} loading={!pipTrend} />
          <AttendanceHotspotsChart
            data={attendanceHotspots}
            loading={!attendanceHotspots}
            onDrill={drillTo}
          />
        </div>
      </div>

      {/* Phase 2 (May 2026) — HR Partner Performance section removed per
          product request. The /api/dashboard/hr-partner-metrics endpoint is
          still registered server-side but is no longer fetched or rendered
          here. Full removal of the endpoint is queued for a later cleanup. */}
    </div>
  );
}
